import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

/**
 * Boton "copiar + aplicar" de la barra de anuncios (Ole).
 *
 * Flujo: copia el codigo al portapapeles y lo aplica al carrito en la misma accion, para que el
 * cliente no tenga que pegarlo en ningun sitio. Usa la misma via nativa que cart-discount.js:
 * POST a Theme.routes.cart_update_url con { discount, sections } y morph de la seccion.
 *
 * Nota importante: con el carrito VACIO Shopify guarda el codigo igualmente y lo devuelve como
 * applicable:false; se activa solo (applicable:true) en cuanto entra el primer producto. Por eso
 * NO tratamos applicable:false como error — es el caso normal al pulsar el banner antes de comprar.
 */

const CART_DRAWER_SECTION = 'cart-drawer-section';
const FEEDBACK_DURATION = 2400;

/** Copia texto al portapapeles, con fallback para contextos sin Clipboard API. */
async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // cae al fallback
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

/** Codigos ya presentes en el carrito, para no pisarlos al aplicar el nuevo. */
async function existingDiscountCodes() {
  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`);
    if (!response.ok) return [];
    const cart = await response.json();
    return (cart.discount_codes || []).map((discount) => discount.code).filter(Boolean);
  } catch {
    return [];
  }
}

/** Aplica el codigo al carrito y refresca el cajon si esta en la pagina. */
async function applyDiscount(code) {
  const existing = await existingDiscountCodes();
  const codes = existing.includes(code) ? existing : [...existing, code];

  const hasDrawer = Boolean(document.getElementById(`shopify-section-${CART_DRAWER_SECTION}`));

  const response = await fetch(
    Theme.routes.cart_update_url,
    fetchConfig('json', {
      body: JSON.stringify({
        discount: codes.join(','),
        ...(hasDrawer ? { sections: [CART_DRAWER_SECTION] } : {}),
      }),
    })
  );

  if (!response.ok) throw new Error(`cart/update respondio ${response.status}`);

  const data = await response.json();

  // Sin morph, un cajon ya renderizado seguiria mostrando el estado anterior hasta la proxima
  // mutacion del carrito. morphSection lanza si la seccion no esta en el DOM, de ahi el guard.
  const html = data.sections?.[CART_DRAWER_SECTION];
  if (hasDrawer && html) {
    try {
      morphSection(CART_DRAWER_SECTION, html, { mode: 'hydration' });
    } catch {
      // el cajon se re-renderiza igualmente al abrirse; no rompemos el feedback por esto
    }
  }
}

function showFeedback(button, message) {
  const feedback = button.querySelector('.announcement-copy__feedback');
  button.dataset.copied = 'true';
  if (feedback) feedback.textContent = message;

  clearTimeout(Number(button.dataset.feedbackTimer));
  const timer = setTimeout(() => {
    delete button.dataset.copied;
    if (feedback) feedback.textContent = '';
  }, FEEDBACK_DURATION);
  button.dataset.feedbackTimer = String(timer);
}

async function handleClick(button) {
  if (button.dataset.busy === 'true') return;

  const code = button.dataset.discountCode;
  if (!code) return;

  button.dataset.busy = 'true';
  try {
    await copyToClipboard(code);
    await applyDiscount(code);
    showFeedback(button, button.getAttribute('data-done-label') || '');
  } catch {
    // Si falla la aplicacion, al menos el codigo quedo copiado: el cliente puede pegarlo en el
    // checkout. No mostramos un error ruidoso en la barra de anuncios.
    showFeedback(button, '');
  } finally {
    delete button.dataset.busy;
  }
}

// Delegacion: el bloque puede repetirse y el modulo se evalua una sola vez por URL.
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest('[data-announcement-copy]');
  if (button instanceof HTMLElement) handleClick(button);
});
